import React from "react";
import { useTranslation } from "react-i18next";
import { PARAMETER_OPTIONS } from "../../../../../../constants";
import MetconDisplay from "../../../../../../parts/metcon/MetconDisplay";
import MetconExercisesList from "../../../../../../parts/metcon/MetconExercisesList";
import TooltipPortal from "../../../../../../parts/tooltip-portal";
import { useRillContext } from "../../../../../../rill/RillReact/share/base";
import {
  anySetCompleted,
  getSetRoundCount,
} from "../../../../../../utils/actuals";
import { formatMetconResult } from "../../../../../../utils/metcon";
import { isPROutcome } from "../../../../../../utils/personal-records";
import Assistant from "./Assistant";
import TableForAssistant from "./TableForAssistant";
import TableRow from "./TableRow";

const { RPE, RPEAMRAP } = PARAMETER_OPTIONS;

const ExerciseContent = ({
  exercise,
  exerciseDate,
  someHasRPE,
  finalSets,
  complexTwo,
  complexThree,
  complexTwoSets,
  complexThreeSets,
  editDayMode,
  editable,
  handleInput,
  repMaximums,
  addTempSet,
  removeSet,
  removeTempSet,
  withWeight,
  fromMetric,
  intensityMeasure,
  isRiR,
  units,
}) => {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useRillContext();
  const isAssistant = exercise.type === "assistant";
  const isMetcon = exercise.primary === "metcon";
  const hasFreeForm = exercise.sets.some((set) => set.freeform);
  // The athlete has logged something: swap prescribed values for performed ones
  // instead of leaving the coach staring at the plan (see TableRow's actuals
  // branch). Deliberately not reusing `editable`, which is a bare truthiness
  // check on `outcomes` and so also locks an exercise carrying an empty `{}`.
  const hasActuals = anySetCompleted(exercise);

  // Which rounds earned a PR, keyed "<setIdx>-<roundIdx>". Resolved here rather
  // than per row because the trailing badge column is an exercise-level decision
  // (the <thead> lives in this file) and because the same rep-max has to be
  // deduped across rows: isPROutcome matches on load, so two identical top sets
  // would both badge the same 1RM. Mirrors MonthExercise's shownPRReps.
  // The loader returns {} for future-dated ranges, hence the repMaxPR guard.
  const repMaxPR = locals?.dayPersonalRecords?.repMaxPR;
  const prByRound = {};
  if (repMaxPR && hasActuals) {
    const shownReps = new Set();
    exercise.sets.forEach((set, setIdx) => {
      for (let i = 0; i < getSetRoundCount(set); i++) {
        const pr = isPROutcome(
          exercise,
          set.outcomes?.[i],
          repMaxPR,
          exerciseDate
        );
        if (pr && !shownReps.has(pr.reps)) {
          shownReps.add(pr.reps);
          prByRound[`${setIdx}-${i}`] = pr.reps;
        }
      }
    });
  }
  const hasPR = Object.keys(prByRound).length > 0;

  if (isMetcon) {
    // A metcon completes as a whole, so there is nothing to swap per set — the
    // athlete's result lives in metconResult. Once it exists, render the same
    // compact block the read-only week view uses (WeekDayExercise), which
    // carries the per-movement ticks and the result line. Left alone until
    // then: .wk-metcon dims the movement rows of an unlogged metcon, which
    // would be wrong for one the coach is still prescribing.
    const metconResult = exercise.metconResult
      ? formatMetconResult(exercise.metconResult)
      : "";

    if (!metconResult) {
      return (
        <>
          <MetconDisplay exercise={exercise} />
          <MetconExercisesList exercise={exercise} setIndex={0} />
        </>
      );
    }

    return (
      <div className="wk-metcon">
        <MetconDisplay exercise={exercise} compact />
        <MetconExercisesList exercise={exercise} setIndex={0} showCompletion />
        <div className="wk-metcon-result">
          <span className="wk-metcon-result-label">
            {t("calendar.metconResult", "Result")}
          </span>
          <span
            className={`wk-metcon-result-value${
              exercise.metconResult?.status === "completed" ? " completed" : ""
            }`}
          >
            {metconResult}
          </span>
        </div>
      </div>
    );
  }

  const isExerciseStarter =
    isAssistant && exercise.primary === "assistant" && !exercise.parameters;

  if (isAssistant && hasFreeForm) {
    const assistantProps = {
      finalSets,
      editDayMode,
      editable,
      hasActuals,
      exerciseId: exercise._id,
      handleInput,
      addTempSet,
      removeSet,
      removeTempSet,
    };

    return editDayMode && !editable ? (
      <TooltipPortal
        childrenContainerClass={"table-editor-with-tooltip"}
        body={t("exerciseEditor.exerciseCompletedNoEdit")}
      >
        <Assistant {...assistantProps} />
      </TooltipPortal>
    ) : (
      <Assistant {...assistantProps} />
    );
  }

  const tableProps = {
    editDayMode,
    editable,
    hasActuals,
    hasPR,
    prByRound,
    isAssistant,
    isExerciseStarter,
    exercise,
    finalSets,
    complexTwo,
    complexThree,
    complexTwoSets,
    complexThreeSets,
    someHasRPE,
    intensityMeasure,
    handleInput,
    repMaximums,
    addTempSet,
    removeSet,
    removeTempSet,
    withWeight,
    fromMetric,
    isRiR,
    units,
  };

  return editDayMode && !editable ? (
    <TooltipPortal
      childrenContainerClass={"table-editor-with-tooltip"}
      body={t("exerciseEditor.exerciseCompletedNoEdit")}
    >
      <Table {...tableProps} />
    </TooltipPortal>
  ) : (
    <Table {...tableProps} />
  );
};

const Table = ({
  editDayMode,
  editable,
  hasActuals,
  hasPR,
  prByRound,
  isAssistant,
  isExerciseStarter,
  exercise,
  finalSets,
  complexTwo,
  complexThree,
  complexTwoSets,
  complexThreeSets,
  someHasRPE,
  intensityMeasure,
  handleEditTemporalInput,
  handleInput,
  repMaximums,
  addTempSet,
  removeSet,
  removeTempSet,
  withWeight,
  fromMetric,
  isRiR,
  units,
}) => {
  // Actuals rows are one per round, numbered 1,2,3… within their own
  // set-group. Each entry in finalSets is a distinct target (e.g. a 2x5
  // followed by a 1x3 is two targets), so numbering restarts at 1 for every
  // target instead of continuing across them — a 2-set target followed by
  // another 2-set target reads as 1,2,1,2, not 1,2,3,4.
  const setNumberOffsets = (finalSets || []).map(() => 0);

  return (
    <table
      className={`${
        editDayMode && editable
          ? "tr-table-editing tr-table"
          : editDayMode
            ? "tr-table not-editable"
            : "tr-table"
      }${hasActuals ? " tr-table-actuals" : ""}`}
    >
      <thead>
        {!isAssistant || isExerciseStarter ? (
          <tr>
            <th>Sets</th>
            <th>Reps</th>
            <th>{isRiR ? "RiR" : "RPE"}</th>
            <th>Load</th>
            {isExerciseStarter ? null : <th>%1RM</th>}
            {/* Unlabelled, like the read-only week view's video column. */}
            {hasPR ? <th className="pr-badge-col" /> : null}
            {exercise.sets.length && editDayMode && editable ? (
              <th className="invisible">-</th>
            ) : null}
          </tr>
        ) : (
          <tr>
            {exercise?.parameters?.map((param = "", idx) => (
              <th key={idx}>
                {[RPE, RPEAMRAP].includes(param) ? intensityMeasure : param}
              </th>
            ))}
            {hasPR ? <th className="pr-badge-col" /> : null}
            {exercise.sets.length && editDayMode && editable ? (
              <th className="invisible">-</th>
            ) : null}
          </tr>
        )}
      </thead>
      <tbody>
        {!isAssistant || isExerciseStarter
          ? finalSets?.map((set, idx) => {
              const paramsRow = {
                exerciseId: exercise._id,
                exerciseHasCustomRPE:
                  exercise.type === "secondary" && exercise.secondaryRepMaximum
                    ? exercise.secondaryRepMaximum
                    : false,
                primary: exercise.primary,
                set,
                handler: handleInput,
                idx: `${exercise._id}-${idx}`,
                someHasRPE,
                hasSets: exercise.sets.length,
                repMaximums,
                addTempSet,
                removeSet,
                removeTempSet,
                editDayMode,
                editable,
                hasActuals,
                hasPR,
                prByRound,
                exercise,
                setNumberOffset: setNumberOffsets[idx],
                withWeight,
                fromMetric,
                isExerciseStarter,
                intensityMeasure,
                isRiR,
                units,
                hasLoadDrop: exercise?.sets?.[idx]?.loadDrop,
                complexTwo,
                complexThree,
                complexTwoSet: complexTwoSets?.[idx],
                complexThreeSet: complexThreeSets?.[idx],
              };

              return <TableRow key={`${exercise._id}-${idx}`} {...paramsRow} />;
            })
          : finalSets?.map((set, idx) => {
              const paramsRow = {
                idx,
                exercise,
                handler: handleInput,
                editDayMode,
                editable,
                hasActuals,
                hasPR,
                prByRound,
                setNumberOffset: setNumberOffsets[idx],
                set,
                isTemporal: false,
                addTempSet,
                removeSet,
                withWeight,
                fromMetric,
                intensityMeasure,
                isRiR,
                units,
                hasLoadDrop: exercise?.sets?.[idx]?.loadDrop,
              };
              return (
                <TableForAssistant
                  key={`${exercise._id}-${idx}`}
                  {...paramsRow}
                />
              );
            })}
      </tbody>
    </table>
  );
};

export default ExerciseContent;
